<template>
  <div>
    <v-card flat>
      <v-card-title class="text-h5 font-weight-bold">
        {{ $t('Localization') }}
      </v-card-title>

      <v-card-text>
        <v-form ref="form">
          <v-row>
            <v-col cols="12">
              <v-card outlined class="pa-4">
                <v-card-title
                  class="text-subtitle-1 px-0 pt-0 d-flex align-center"
                >
                  <span>{{ $t('Localized Content') }}</span>
                  <v-spacer />
                  <v-btn
                    color="primary"
                    class="ml-2"
                    :loading="translating"
                    @click="handleTranslate"
                  >
                    {{ $t('Translate Content') }}
                  </v-btn>
                </v-card-title>

                <v-expansion-panels multiple>
                  <v-expansion-panel v-for="lang in localeList" :key="lang">
                    <v-expansion-panel-header>
                      <div class="d-flex align-center">
                        <v-chip small class="mr-2">{{ lang }}</v-chip>
                        <span class="text-body-2">{{ localeName(lang) }}</span>
                      </div>
                    </v-expansion-panel-header>
                    <v-expansion-panel-content>
                      <v-list two-line dense>
                        <v-list-item
                          v-for="def in localizationKeys"
                          :key="def.key"
                          class="px-0"
                        >
                          <v-list-item-content>
                            <v-list-item-title class="text-body-2 mb-1">
                              {{ def.label || def.key }}
                            </v-list-item-title>
                            <div>
                              <v-text-field
                                v-if="def.type === 'text'"
                                :value="getValue(lang, def.key)"
                                outlined
                                dense
                                @input="val => setValue(lang, def.key, val)"
                              />
                              <v-textarea
                                v-else-if="def.type === 'textarea'"
                                :value="getValue(lang, def.key)"
                                auto-grow
                                outlined
                                rows="4"
                                @input="val => setValue(lang, def.key, val)"
                              />
                              <text-editor
                                v-else-if="def.type === 'html'"
                                :value="getValue(lang, def.key)"
                                :show-save-button="false"
                                :init-full="true"
                                @input="val => setValue(lang, def.key, val)"
                              />
                            </div>
                          </v-list-item-content>
                        </v-list-item>
                      </v-list>
                    </v-expansion-panel-content>
                  </v-expansion-panel>
                </v-expansion-panels>
              </v-card>
            </v-col>
          </v-row>
        </v-form>
      </v-card-text>
    </v-card>

    <v-overlay :value="translating" opacity="0.5">
      <v-card class="pa-6 text-center">
        <v-progress-circular indeterminate color="primary" class="mb-3" />
        <div class="text-subtitle-1">
          {{
            $t(
              'Translation is in progress, please wait. This may take a few minutes.'
            )
          }}
        </div>
      </v-card>
    </v-overlay>
  </div>
</template>

<script>
import axios from '@/axios/config'
import TextEditor from 'components/common/text-editor'
import { debounce } from 'lodash'

const LOCALES = [
  'ar',
  'de',
  'es',
  'fr',
  'he',
  'it',
  'ru',
  'sv',
  'th',
  'zh',
  'pl',
]

export default {
  name: 'Localization',

  components: { TextEditor },

  props: {
    bookingEngineSetting: {
      type: Object,
      required: true,
    },
  },

  data() {
    const seed = this.bookingEngineSetting.localization || {}
    const prepared = {}
    LOCALES.forEach(lang => {
      const sections = seed[lang]?.sections || {}
      const pages = seed[lang]?.pages || {}
      prepared[lang] = {
        sections,
        pages,
      }
    })
    return {
      formData: {
        localization: prepared,
      },
      translating: false,
      emitUpdateDebounced: null,
    }
  },

  computed: {
    localeList() {
      return LOCALES
    },
    localizationKeys() {
      return [
        {
          key: 'sections.header.title',
          type: 'text',
          label: 'Landing Header Title',
        },
        {
          key: 'sections.header.sub_title',
          type: 'text',
          label: 'Landing Header Subtitle',
        },
        {
          key: 'pages.about.header.story',
          type: 'textarea',
          label: 'About Page Header Story',
        },
        {
          key: 'pages.about.header.title',
          type: 'text',
          label: 'About Page Header Title',
        },
        {
          key: 'pages.about.header.sub_title',
          type: 'text',
          label: 'About Page Header Sub Title',
        },
        {
          key: 'pages.terms.content',
          type: 'html',
          label: 'Terms Page Content',
        },
        {
          key: 'pages.privacy.content',
          type: 'html',
          label: 'Privacy Page Content',
        },
        {
          key: 'pages.exit.content.title',
          type: 'text',
          label: 'Exit Page Title',
        },
        {
          key: 'pages.exit.content.subtitle',
          type: 'text',
          label: 'Exit Page Subtitle',
        },
        {
          key: 'pages.exit.content.discountSubtitle',
          type: 'text',
          label: 'Exit Page Discount Subtitle',
        },
      ]
    },
  },

  created() {
    this.emitUpdateDebounced = debounce(this.emitUpdate, 300)
  },

  methods: {
    localeName(code) {
      const names = {
        ar: 'Arabic',
        de: 'German',
        es: 'Spanish',
        fr: 'French',
        he: 'Hebrew',
        it: 'Italian',
        ru: 'Russian',
        sv: 'Swedish',
        th: 'Thai',
        zh: 'Chinese',
        pl: 'Polish',
      }
      return names[code] || code
    },

    // Value helpers for nested keys like 'sections.landing.header.title'
    getValue(lang, path) {
      const { root, parts } = this.splitPath(path)
      const base = this.formData.localization[lang][root] || {}
      return this.getDeep(base, parts) || ''
    },

    setValue(lang, path, value) {
      const { root, parts } = this.splitPath(path)
      if (!this.formData.localization[lang][root]) {
        this.$set(this.formData.localization[lang], root, {})
      }
      const base = this.formData.localization[lang][root]
      this.setDeep(base, parts, value)
      this.emitUpdateDebounced()
    },

    splitPath(path) {
      const segments = (path || '').split('.')
      const root = segments.shift() // 'sections' or 'pages'
      return { root, parts: segments }
    },

    getDeep(obj, parts) {
      return parts.reduce(
        (acc, key) => (acc && acc[key] !== undefined ? acc[key] : undefined),
        obj
      )
    },

    setDeep(obj, parts, value) {
      let cursor = obj
      parts.forEach((key, idx) => {
        if (idx === parts.length - 1) {
          this.$set(cursor, key, value)
        } else {
          if (
            cursor[key] === undefined ||
            cursor[key] === null ||
            typeof cursor[key] !== 'object'
          ) {
            this.$set(cursor, key, {})
          }
          cursor = cursor[key]
        }
      })
    },

    emitUpdate() {
      // Normalize payload to { lang: { sections, pages } }
      const payload = {}
      LOCALES.forEach(lang => {
        const entry = this.formData.localization[lang]
        payload[lang] = {
          sections: entry.sections || {},
          pages: entry.pages || {},
        }
      })
      this.$emit('update:setting', { localization: payload })
    },

    async handleTranslate() {
      if (!this.bookingEngineSetting?.id) return
      try {
        this.translating = true
        const { data } = await axios.post(
          `/api/booking-engine-settings/${this.bookingEngineSetting.id}/translate-content`
        )
        // Expecting { localization: { [lang]: { sections, pages } } }
        const loc = data?.localization || {}
        LOCALES.forEach(lang => {
          const sections = loc[lang]?.sections || {}
          const pages = loc[lang]?.pages || {}
          this.formData.localization[lang].sections = sections
          this.formData.localization[lang].pages = pages
        })
        this.emitUpdate()
      } catch (e) {
        console.error(e)
      } finally {
        this.translating = false
      }
    },
  },
}
</script>

<style scoped></style>
